import { Link } from 'react-router'
import { DatePicker } from '@klear/ui/components/date-picker'
import { Modal } from '@klear/ui/components/modal'
import { RadioGroup, RadioGroupItem } from '@klear/ui/components/radio-group'
import { Label } from '@klear/ui/components/label'
import { ROUTE_ORDERS, ROUTE_ORDER_EVENTS, parseOrderIdFromDisplay } from '@klear/constants'
import type { Money } from '@klear/money'
import { KlearDate } from '@klear/date'
import { type ICellRendererParams } from 'ag-grid-community'
import toNumber from 'lodash/toNumber'
import toString from 'lodash/toString'
import isArray from 'lodash/isArray'
import isObject from 'lodash/isObject'
import isString from 'lodash/isString'

export type DateRangeOption = 'current-month' | 'last-month' | 'this-year' | 'last-year' | 'all-time' | 'date-range'

export type ReportViewSelection = {
  option: DateRangeOption
  startDate?: string
  endDate?: string
}

export const DATE_RANGE_LABELS: Record<DateRangeOption, string> = {
  'current-month': 'This month',
  'last-month': 'Last month',
  'this-year': 'This year',
  'last-year': 'Last year',
  'all-time': 'All time',
  'date-range': 'Custom range'
}

export type ReportPageSize = 'all' | 50 | 100 | 500 | 1000
export const REPORT_PAGE_SIZE_OPTIONS: ReportPageSize[] = ['all', 50, 100, 500, 1000]

export function resolveDateRange(
  option: DateRangeOption,
  start: KlearDate | undefined,
  end: KlearDate | undefined
): { startDate?: KlearDate; endDate?: KlearDate } {
  const today = KlearDate.today()
  switch (option) {
    case 'current-month':
      return { startDate: today.startOf('month'), endDate: today.endOf('month') }
    case 'last-month': {
      const last = today.subtract(1, 'month')
      return { startDate: last.startOf('month'), endDate: last.endOf('month') }
    }
    case 'this-year':
      return { startDate: today.startOf('year'), endDate: today.endOf('year') }
    case 'last-year': {
      const last = today.subtract(1, 'year')
      return { startDate: last.startOf('year'), endDate: last.endOf('year') }
    }
    case 'all-time':
      return { startDate: KlearDate.of('1900-01-01'), endDate: today.add(1, 'day') }
    case 'date-range':
      return { startDate: start, endDate: end }
  }
}

export function defaultReportViewSelection(option: DateRangeOption = 'current-month'): ReportViewSelection {
  const { startDate, endDate } = resolveDateRange(option, undefined, undefined)
  if (option === 'all-time') {
    return { option }
  }
  return {
    option,
    startDate: startDate?.toJSON(),
    endDate: endDate?.toJSON()
  }
}

export function reportViewSummary(selection: ReportViewSelection | null): string | null {
  if (!selection) return null
  const label = DATE_RANGE_LABELS[selection.option]
  const range = selection.startDate && selection.endDate ? `${selection.startDate} → ${selection.endDate}` : null
  return range ? `${label} (${range})` : label
}

export function loadReportPageSize(storageKey: string): ReportPageSize {
  if (typeof window === 'undefined') return 'all'
  try {
    const raw = window.localStorage.getItem(storageKey)
    if (raw === 'all') return 'all'
    if (raw != null) {
      const n = toNumber(raw)
      if (REPORT_PAGE_SIZE_OPTIONS.includes(n as ReportPageSize)) return n as ReportPageSize
    }
  } catch {}
  return 'all'
}

export function loadHiddenReportColumns(storageKey: string): Set<string> {
  if (typeof window === 'undefined') return new Set()
  try {
    const raw = window.localStorage.getItem(storageKey)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw)
    if (isArray(parsed)) return new Set(parsed.filter((v): v is string => isString(v)))
  } catch {}
  return new Set()
}

function GridLinkCell({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="text-foreground hover:text-foreground underline font-medium"
      onClick={(e) => e.stopPropagation()}
    >
      {label}
    </Link>
  )
}

export function OrderLinkCell(p: ICellRendererParams) {
  const value = p.value
  if (value == null || value === '') return null
  const label = toString(value)
  if (parseOrderIdFromDisplay(label) == null) return label
  return <GridLinkCell to={`/${ROUTE_ORDERS}/${label}`} label={label} />
}

const ORDER_EVENT_ID_DISPLAY = /^[A-Z]+-\d+$/

export function OrderEventLinkCell(p: ICellRendererParams) {
  const value = p.value
  if (value == null || value === '') return null
  const label = toString(value)
  if (!ORDER_EVENT_ID_DISPLAY.test(label) || parseOrderIdFromDisplay(label) != null) return label
  return <GridLinkCell to={`/${ROUTE_ORDER_EVENTS}/${label}`} label={label} />
}

export function downloadCsvText(csv: string, filename: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}

function escapeCsvCell(value: string | number | Money | null | undefined): string {
  let text = ''
  if (value != null) {
    text = isObject(value) ? value.toString() : toString(value)
  }
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`
  }
  return text
}

export function reportDataToCsv(
  data: {
    headers: string[]
    rows: (string | number | Money)[][]
    columns: { index: number; title: string }[]
  },
  hiddenColumns: ReadonlySet<string> = new Set(),
  columnOrder?: readonly string[] | null
): string {
  const visible = data.columns.filter((col) => !hiddenColumns.has(toString(col.index)))
  const byField = new Map(visible.map((col) => [toString(col.index), col]))
  const ordered: { index: number; title: string }[] = []
  if (columnOrder?.length) {
    for (const field of columnOrder) {
      const col = byField.get(field)
      if (!col) continue
      ordered.push(col)
      byField.delete(field)
    }
  }
  for (const col of byField.values()) ordered.push(col)

  const headerLine = ordered.map((col) => escapeCsvCell(col.title)).join(',')
  const rowLines = data.rows.map((row) => ordered.map((col) => escapeCsvCell(row[col.index] ?? '')).join(','))
  return [headerLine, ...rowLines].join('\n')
}

export function reportGridColumnOrder(
  gridApi: { getColumnState: () => { colId?: string | null }[] } | null | undefined
): string[] {
  if (!gridApi) return []
  return gridApi
    .getColumnState()
    .map((col) => col.colId)
    .filter((id): id is string => id != null && id !== '')
}

type ReportDateRangeModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  primaryText: string
  idPrefix: string
  dateRangeOption: DateRangeOption
  onDateRangeOptionChange: (option: DateRangeOption) => void
  customStart: KlearDate | undefined
  customEnd: KlearDate | undefined
  onCustomStartChange: (date: KlearDate | undefined) => void
  onCustomEndChange: (date: KlearDate | undefined) => void
  onConfirm: () => void
  primaryDisabled: boolean
}

export function ReportDateRangeModal({
  open,
  onOpenChange,
  title,
  primaryText,
  idPrefix,
  dateRangeOption,
  onDateRangeOptionChange,
  customStart,
  customEnd,
  onCustomStartChange,
  onCustomEndChange,
  onConfirm,
  primaryDisabled
}: ReportDateRangeModalProps) {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description="Select a date range."
      size="sm"
      primaryText={primaryText}
      handlePrimaryAction={onConfirm}
      primaryDisabled={primaryDisabled}
      secondaryText="Cancel"
      handleSecondaryAction={() => onOpenChange(false)}
    >
      <div className="space-y-6">
        <RadioGroup
          value={dateRangeOption}
          onValueChange={(value) => onDateRangeOptionChange(value as DateRangeOption)}
        >
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="current-month" id={`${idPrefix}-current-month`} />
              <Label htmlFor={`${idPrefix}-current-month`} className="cursor-pointer">
                This month
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="last-month" id={`${idPrefix}-last-month`} />
              <Label htmlFor={`${idPrefix}-last-month`} className="cursor-pointer">
                Last month
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="this-year" id={`${idPrefix}-this-year`} />
              <Label htmlFor={`${idPrefix}-this-year`} className="cursor-pointer">
                This year
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="last-year" id={`${idPrefix}-last-year`} />
              <Label htmlFor={`${idPrefix}-last-year`} className="cursor-pointer">
                Last year
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="all-time" id={`${idPrefix}-all-time`} />
              <Label htmlFor={`${idPrefix}-all-time`} className="cursor-pointer">
                All time
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="date-range" id={`${idPrefix}-date-range`} />
              <Label htmlFor={`${idPrefix}-date-range`} className="cursor-pointer">
                Date range
              </Label>
            </div>
          </div>
        </RadioGroup>

        {dateRangeOption === 'date-range' && (
          <div className="space-y-4 pl-6">
            <div className="space-y-2">
              <Label>Start date</Label>
              <DatePicker value={customStart} onChange={onCustomStartChange} />
            </div>
            <div className="space-y-2">
              <Label>End date</Label>
              <DatePicker value={customEnd} onChange={onCustomEndChange} />
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

export function resolveExposureAsOfDate(viewSelection: ReportViewSelection): string {
  const requested = viewSelection.endDate ?? KlearDate.today().toJSON()
  const today = KlearDate.today().toJSON()
  return requested > today ? today : requested
}

export function exposureReplayEndDate(asOf: string): string | undefined {
  if (asOf >= KlearDate.today().toJSON()) return undefined
  return asOf
}

export function reportQueryInput(selection: ReportViewSelection) {
  return selection.option === 'all-time' ? {} : { startDate: selection.startDate, endDate: selection.endDate }
}
